import { useCallback, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { getAppCheckTokenHeaders, isFirebaseError } from '@/firebase';
import { UIToast } from '@/ui';
import { useMeTranslation } from '../hooks';

export type ResetPasswordVariables = {
    token: string;
    password: string;
};

type UseResetPasswordActionData = {
    resetPassword: (variables: ResetPasswordVariables) => Promise<void>;
    loading: boolean;
    error: string;
    /** Clears the message so a fresh attempt starts on a clean form. */
    clearError: () => void;
};

/** Setting a new password from the link in the reset email. */
export function useResetPasswordAction(): UseResetPasswordActionData {
    const { t } = useMeTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error `POST /v3/auth/reset-password`
    // declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            // Thrown before the request, while minting the App Check token.
            if (isFirebaseError(error)) {
                return t('shared.errors.app_check_failed');
            }

            if (isApiError(error, 'APP_CHECK_FAILED')) {
                return t('shared.errors.app_check_failed');
            }
            if (isApiError(error, 'INVALID_TOKEN')) {
                return t('actions.reset_password.error_invalid_token');
            }
            if (isApiError(error, 'TOO_MANY_REQUESTS')) {
                return t('shared.errors.too_many_requests');
            }
            if (isApiError(error, 'USER_NOT_FOUND')) {
                return t('shared.errors.user_not_found');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: ({ token, password }: ResetPasswordVariables) =>
            // The endpoint is App Check protected, so the token has to be
            // fetched before the request rather than added by an interceptor.
            getAppCheckTokenHeaders()
                .then((headers) =>
                    openApiRef.current.auth.resetPassword(
                        { token, password },
                        { headers }
                    )
                )
                .then(() => {}),
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        resetPassword: (variables) => mutation.mutateAsync(variables),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : '',
        clearError: () => mutation.reset()
    };
}
